import {
  MainContainer,
  MessageContainer,
  MessageHeader,
  MessageInput,
  MessageList,
  MinChatUiProvider,
} from "@minchat/react-chat-ui";
import { useNavigate, useParams } from "react-router-dom";
import { colors } from "../../../theme/colors";
import { styled } from "styled-components";
import { useEffect, useRef, useState } from "react";
import { Ticket } from "../../../models/ticket.model";
import { axios } from "../../../api/axios";
import { handleResponse } from "../../../utils/functions/http/handleResponse";
import _ from "lodash";
import { useAtomValue } from "jotai";
import { authAtom } from "../../../atoms/auth.atom";
import { getFileTypeFromUrl } from "../../../utils/functions/file/getFileTypeFromUrl";
import { Pressable } from "../../../components/ui/pressable/pressable.component";
import { MdAttachFile, MdClose, MdSend } from "react-icons/md";
import { Row } from "../../../components/ui/grid/row.component";
import { Page } from "../../../components/layout/page/page.component";
import { Input } from "antd";
import TextArea from "antd/es/input/TextArea";

export const ViewTicket = () => {
  const { id: ticketId } = useParams();
  const [ticket, setTicket] = useState<Ticket | null>(null);

  const fileInput = useRef<HTMLInputElement | null>(null);
  const [files, setFiles] = useState<{ file: File; url: string }[]>([]);

  const [message, setMessage] = useState("");

  const auth = useAtomValue(authAtom);

  const navigate = useNavigate();

  useEffect(() => {
    if (ticketId) {
      axios
        .get<Ticket>(`/client/tickets/${ticketId}`)
        .then((response) => {
          const ticket = { ...response.data };
          const newConversation: Ticket["conversation"] = [];

          for (const message of ticket.conversation) {
            if (message.files.length > 1) {
              for (let i = 0; i < message.files.length; i++) {
                newConversation.push({
                  ...message,
                  message: undefined,
                  files: [message.files[i]],
                });
              }
              newConversation.push({
                ...message,
                files: [],
              });
            } else {
              newConversation.push({ ...message, message: undefined });
              newConversation.push({ ...message, files: [] });
            }
          }

          ticket.conversation = newConversation;

          setTicket(ticket);
        })
        .catch(handleResponse);
    }
  }, [ticketId]);

  return (
    <Page noPadding>
      <MinChatUiProvider
        theme={colors.primary.hex}
        colorSet={{
          "--input-background-color": colors.background.hex,
          "--input-placeholder-color": colors.text.withOpacity(0.7).hex,
          "--input-text-color": colors.text.hex,
          "--outgoing-message-text-color": colors.white.hex,
          "--container-background-color": colors.background.hex,
          "--chatlist-header-background-color": colors.background.hex,
          "--chatlist-background-color": colors.background.hex,
          "--messagelist-background-color": colors.background.hex,
          "--message-header-background-color": colors.background.hex,
          "--message-header-text-color": colors.text.hex,
          "--message-header-back-color": colors.text.hex,
          "--outgoing-message-checkmark-color": colors.text.withOpacity(0).hex,
          "--incoming-message-link-color": colors.complement.hex,
          "--outgoing-message-link-color": colors.complement.hex,
          "--incoming-message-text-color": colors.text.hex,
        }}
      >
        <input
          type="file"
          ref={fileInput}
          style={{ display: "none" }}
          onChange={(evt) => {
            const newFiles = evt.target.files ?? [];
            if (!newFiles.length) return;

            const file = newFiles[0];
            setFiles([...files, { file, url: URL.createObjectURL(file) }]);

            setMessage(`${file.name} attached`);
            // const messageInput = document.querySelector(
            //   'div[data-testid="message-input"]'
            // ) as HTMLDivElement | undefined;
            // if (messageInput && !messageInput.innerText) {
            //   messageInput.innerText = `${file.name} attached`;
            //   const placeholderNode = messageInput.parentNode?.childNodes[1] as
            //     | HTMLDivElement
            //     | undefined;
            //   if (placeholderNode) placeholderNode.innerText = "";
            // }
          }}
        />
        <MainContainer style={{ height: "100vh", whiteSpace: "pre-line" }}>
          <AttachedFilesContainer>
            {files.map(({ file, url }, i) => (
              <AttachedFile>
                {file.type.startsWith("image/") ? (
                  <img src={url} alt={file.name} />
                ) : (
                  <Row style={{ padding: "1rem" }} $gap={15}>
                    <MdAttachFile size={20} color={colors.black.hex} />
                    <a href={url} target="_blank">
                      {file.name}
                    </a>
                  </Row>
                )}
                <RemoveButton
                  onClick={() => {
                    const newFiles = [...files];
                    newFiles.splice(i, 1);

                    setFiles(newFiles);
                  }}
                >
                  <MdClose size={15} color={colors.white.hex} />
                </RemoveButton>
              </AttachedFile>
            ))}
          </AttachedFilesContainer>
          <MessageContainer>
            <MessageHeader showBack onBack={() => navigate(-1)}>
              <Title>
                {_.truncate(ticket?.title ?? "Untitled ticket", { length: 25 })}
              </Title>
            </MessageHeader>
            <MessageList
              currentUserId={auth.user?.id ?? "me"}
              messages={(ticket?.conversation ?? [])
                .filter((message) => message.message || message.files.length)
                .map((message) => ({
                  text: message.message,
                  user: {
                    id: message.sender,
                    name:
                      message.sender === auth.user?.id ? "Me" : "Pocket Films",
                    avatar:
                      message.sender === "Pocket Films"
                        ? "/images/brand/logo-vertical.png"
                        : "/images/ui/avatar.png",
                  },
                  ...(message.files.length
                    ? {
                        media: {
                          url: message.files[0].file,
                          type: getFileTypeFromUrl(message.files[0].file),
                        },
                      }
                    : {}),
                }))}
            />
            <Row
              style={{
                width: "100%",
                backgroundColor: colors.lightBackground.hex,
                padding: "1rem",
                border: "1px solid #eeeeee7d",
                borderRadius: "0.5rem",
                paddingRight: "2rem",
              }}
              $gap={20}
            >
              <Pressable onClick={() => fileInput.current?.click()}>
                <MdAttachFile size={25} />
              </Pressable>
              <TextArea
                placeholder="Type message here..."
                style={{ flex: 1 }}
                onChange={(evt) => setMessage(evt.target.value)}
                autoSize={{ maxRows: 5 }}
                value={message}
              />
              <Pressable
                $disabled={!message}
                onClick={() => {
                  if (!ticketId) return;

                  const formData = new FormData();
                  formData.append("message", message);
                  files.forEach(({ file }) => {
                    formData.append("files", file);
                  });

                  axios
                    .post<Ticket["conversation"][number]>(
                      `/client/tickets/${ticketId}`,
                      formData
                    )
                    .then((resp) => {
                      if (!ticket) return;

                      const newTicket = { ...ticket };
                      const newConversation = [...newTicket.conversation];

                      const message = resp.data;
                      if (message.files.length > 1) {
                        for (let i = 0; i < message.files.length; i++) {
                          newConversation.push({
                            ...message,
                            message: undefined,
                            files: [message.files[i]],
                          });
                        }
                        newConversation.push({
                          ...message,
                          files: [],
                        });
                      } else {
                        newConversation.push({
                          ...message,
                          message: undefined,
                        });
                        newConversation.push({ ...message, files: [] });
                      }

                      newTicket.conversation = newConversation;

                      setTicket(newTicket);
                      setFiles([]);
                      setMessage("");
                    })
                    .catch(handleResponse);
                }}
              >
                <MdSend size={25} />
              </Pressable>
            </Row>
            {/* <MessageInput
              placeholder="Type message here"
              showSendButton
              onAttachClick={() => fileInput.current?.click()}
              onSendMessage={(message) => {
                if (!ticketId) return;

                const formData = new FormData();
                formData.append("message", message);
                files.forEach(({ file }) => {
                  formData.append("files", file);
                });

                axios
                  .post<Ticket["conversation"][number]>(
                    `/client/tickets/${ticketId}`,
                    formData
                  )
                  .then((resp) => {
                    if (!ticket) return;

                    const newTicket = { ...ticket };
                    const newConversation = [...newTicket.conversation];

                    const message = resp.data;
                    if (message.files.length > 1) {
                      for (let i = 0; i < message.files.length; i++) {
                        newConversation.push({
                          ...message,
                          message: undefined,
                          files: [message.files[i]],
                        });
                      }
                      newConversation.push({
                        ...message,
                        files: [],
                      });
                    } else {
                      newConversation.push({ ...message, message: undefined });
                      newConversation.push({ ...message, files: [] });
                    }

                    newTicket.conversation = newConversation;

                    setTicket(newTicket);
                    setFiles([]);
                  })
                  .catch(handleResponse);
              }}
            /> */}
          </MessageContainer>
        </MainContainer>
      </MinChatUiProvider>
    </Page>
  );
};

const Title = styled.h3`
  /* position: absolute;
  top: 2rem;
  right: 2rem; */
  font-size: 1.5rem;
  font-weight: 500;
`;

const AttachedFilesContainer = styled.div`
  display: flex;
  gap: 2rem;
  position: absolute;
  left: 50%;
  bottom: 8rem;
  transform: translateX(-50%);
  z-index: 5;
`;

const AttachedFile = styled.div`
  display: flex;
  position: relative;
  gap: 1.5rem;
  padding: 0.5rem;
  border-radius: 0.5rem;
  background-color: ${colors.grey.hex};

  font-size: 1.5rem;
  font-weight: 500;

  & img {
    height: 10rem;
    cursor: pointer;
  }
`;

const RemoveButton = styled(Pressable)`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: -1.25rem;
  right: -1.25rem;
  width: 2.5rem;
  height: 2.5rem;
  background-color: ${colors.error.hex};
  border-radius: 50%;
`;
